import { Link } from "@tanstack/react-router";
import prima1 from "@/assets/prima1.webp.asset.json";
import dopo1 from "@/assets/dopo1.webp.asset.json";
import prima2 from "@/assets/prima2.webp.asset.json";
import dopo2 from "@/assets/dopo2.webp.asset.json";
import prima3 from "@/assets/prima3.webp.asset.json";
import dopo3 from "@/assets/dopo3.webp.asset.json";
import { BeforeAfter } from "@/components/BeforeAfter";
import { PreventivoForm } from "@/components/PreventivoForm";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { SocialLinks } from "@/components/Social";
import { ANNI_ESPERIENZA, ANNO_FONDAZIONE, EMAIL, TEL_ANTONIO, TEL_LUCA, WHATSAPP_URL } from "@/lib/site";
import type { PaginaProvincia } from "@/lib/province-pagine";

const servizi = [
  {
    titolo: "Lavaggio e trattamento del cotto",
    testo:
      "Lavaggio profondo, deceratura, rimozione di sali e vecchi residui, poi trattamento a cera o protettivo traspirante antimacchia, in interno e in esterno.",
  },
  {
    titolo: "Marmi, pietre e graniglie: lavaggio e protettivo",
    testo:
      "Solo lavaggio e trattamento protettivo di pavimenti, scale e davanzali. Non eseguiamo levigatura, lucidatura a piombo né cristallizzazione.",
  },
  {
    titolo: "Grès e cemento",
    testo: "Rimozione di aloni, velo di posa e sporco incrostato, con protezione antimacchia dove serve.",
  },
];


export function PaginaProvinciaView({ dati, url }: { dati: PaginaProvincia; url: string }) {
  const lavori = [
    { label: "Cortile in cotto lavato e protetto", before: prima1.url, after: dopo1.url },
    { label: "Pavimento interno in cotto recuperato", before: prima2.url, after: dopo2.url },
    { label: "Scala esterna riportata a nuovo", before: prima3.url, after: dopo3.url },
  ];

  return (
    <div className="min-h-screen bg-background">
      <SiteHeader />

      <main>
        <section className="gradient-warm py-16 text-background sm:py-20">
          <div className="mx-auto max-w-6xl px-5">
            <nav aria-label="Percorso" className="text-sm text-background/70">
              <Link to="/" className="underline-offset-4 hover:underline">Home</Link>
              <span className="px-2">/</span>
              <span>{dati.citta}</span>
            </nav>
            <p className="eyebrow mt-6 text-sand">Dal {ANNO_FONDAZIONE} · provincia di {dati.citta}</p>
            <h1 className="mt-4 max-w-3xl text-4xl leading-[1.08] font-semibold sm:text-5xl">
              Lavaggio e trattamento pavimenti in cotto a {dati.citta}
            </h1>
            <p className="mt-5 max-w-2xl text-base text-background/90 sm:text-lg">
              {dati.intro} Lavaggio, deceratura e trattamento protettivo di cotto, marmi, pietre,
              graniglie e grès a {dati.citta} e provincia:{" "}{ANNI_ESPERIENZA} di lavoro artigianale,
              superficie per superficie.
            </p>

            <div className="mt-8 flex flex-wrap gap-3">
              <a
                href="#contatti"
                className="rounded-xs bg-copper px-6 py-3 text-sm font-semibold text-accent-foreground transition-transform hover:-translate-y-0.5"
              >
                Preventivo gratuito a {dati.citta}
              </a>
              <a
                href={`tel:${TEL_ANTONIO}`}
                className="rounded-xs border border-background/40 px-6 py-3 text-sm font-semibold transition-colors hover:bg-background/10"
              >
                Chiama Antonio: 339 751 7813
              </a>
            </div>
          </div>
        </section>

        <section className="mx-auto max-w-6xl px-5 py-20">
          <div className="grid gap-12 md:grid-cols-2">
            <div>
              <p className="eyebrow">Il cotto a {dati.citta}</p>
              <h2 className="mt-3 text-3xl font-semibold sm:text-4xl">
                Ogni zona ha i suoi pavimenti.
              </h2>
              <p className="mt-5 text-base leading-relaxed text-muted-foreground">{dati.contesto}</p>
              <p className="mt-4 text-base leading-relaxed text-muted-foreground">
                {dati.distanza} Prima di iniziare valutiamo sempre di persona la superficie: il
                preventivo che riceverai è scritto, dettagliato e senza impegno.
              </p>
              <p className="mt-6 text-sm">
                <Link to="/trattamento-cotto-esterno" className="text-primary underline underline-offset-4">
                  Hai un cortile o una terrazza? Vedi il trattamento del cotto esterno
                </Link>
              </p>
            </div>
            <div className="surface-warm self-start rounded-sm border border-border p-7">
              <p className="eyebrow">Zone servite</p>
              <ul className="mt-5 space-y-3 text-sm">
                {dati.zone.map((z) => (
                  <li key={z} className="flex gap-3 border-b border-border pb-3 last:border-0 last:pb-0">
                    <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-copper" />
                    <span>{z}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        <section className="bg-secondary/40 py-20">
          <div className="mx-auto max-w-6xl px-5">
            <p className="eyebrow">Servizi a {dati.citta} e provincia</p>
            <h2 className="mt-3 max-w-2xl text-3xl font-semibold sm:text-4xl">
              Lavaggi e trattamenti protettivi, niente levigature.
            </h2>
            <p className="mt-4 max-w-2xl text-base leading-relaxed text-muted-foreground">
              Il nostro mestiere è pulire a fondo e proteggere le superfici: lavaggio, deceratura e
              trattamento finale. Non facciamo lavorazioni meccaniche di asportazione come levigatura,
              lucidatura a piombo o cristallizzazione.
            </p>
            <div className="mt-12 grid gap-6 md:grid-cols-3">
              {servizi.map((s, i) => (
                <article key={s.titolo} className="surface-warm rounded-sm border border-border p-7">
                  <span className="font-display text-sm font-semibold text-copper">0{i + 1}</span>
                  <h3 className="mt-3 text-xl font-semibold">{s.titolo}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{s.testo}</p>
                </article>
              ))}
            </div>

            {dati.serviziLocali && dati.serviziLocali.length > 0 && (
              <div className="mt-16">
                <h3 className="text-2xl font-semibold sm:text-3xl">
                  Come lavoriamo nel {dati.citta === "Brescia" ? "Bresciano" : `territorio di ${dati.citta}`}
                </h3>
                <div className="mt-8 grid gap-6 md:grid-cols-2">
                  {dati.serviziLocali.map((s) => (
                    <article key={s.titolo} className="rounded-sm border border-border bg-background p-7">
                      <h4 className="text-lg font-semibold">{s.titolo}</h4>
                      <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{s.testo}</p>
                      {s.link && (
                        <p className="mt-3 text-sm">
                          <Link
                            to={s.link.to}
                            className="text-primary underline underline-offset-4 hover:text-copper"
                          >
                            {s.link.label}
                          </Link>
                        </p>
                      )}
                    </article>
                  ))}

                </div>
              </div>
            )}
          </div>
        </section>

        {dati.casi && dati.casi.length > 0 && (
          <section className="mx-auto max-w-6xl px-5 py-20">
            <p className="eyebrow">Casi reali in provincia di {dati.citta}</p>
            <h2 className="mt-3 max-w-2xl text-3xl font-semibold sm:text-4xl">
              Tre lavori raccontati per intero.
            </h2>
            <div className="mt-12 grid gap-6 md:grid-cols-3">
              {dati.casi.map((c) => (
                <article key={c.titolo} className="surface-warm flex flex-col rounded-sm border border-border p-7">
                  <p className="eyebrow text-copper">{c.luogo}</p>
                  <h3 className="mt-3 text-xl font-semibold">{c.titolo}</h3>
                  <dl className="mt-5 space-y-4 text-sm leading-relaxed text-muted-foreground">
                    <div>
                      <dt className="font-semibold text-foreground">Situazione</dt>
                      <dd className="mt-1">{c.problema}</dd>
                    </div>
                    <div>
                      <dt className="font-semibold text-foreground">Intervento</dt>
                      <dd className="mt-1">{c.intervento}</dd>
                    </div>
                    <div>
                      <dt className="font-semibold text-foreground">Risultato</dt>
                      <dd className="mt-1">{c.risultato}</dd>
                    </div>
                  </dl>
                </article>
              ))}
            </div>
          </section>
        )}

        {dati.linkUtili && dati.linkUtili.length > 0 && (
          <section className="bg-secondary/40 py-16">
            <div className="mx-auto max-w-6xl px-5">
              <p className="eyebrow">Servizi correlati</p>
              <h2 className="mt-3 max-w-2xl text-2xl font-semibold sm:text-3xl">
                Altri interventi che potrebbero interessarti.
              </h2>
              <div className="mt-8 grid gap-4 md:grid-cols-3">
                {dati.linkUtili.map((l) => (
                  <article
                    key={l.to}
                    className="surface-warm flex flex-col rounded-sm border border-border p-6 transition-colors hover:border-primary"
                  >
                    <h3 className="text-lg font-semibold">
                      <Link
                        to={l.to}
                        className="text-primary underline underline-offset-4 hover:text-copper"
                      >
                        {l.label}
                      </Link>
                    </h3>
                    <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{l.testo}</p>
                  </article>
                ))}
              </div>
            </div>
          </section>
        )}

        {dati.comuni && dati.comuni.length > 0 && (

          <section className="bg-secondary/40 py-16">
            <div className="mx-auto max-w-6xl px-5">
              <p className="eyebrow">Dove interveniamo</p>
              <h2 className="mt-3 max-w-2xl text-2xl font-semibold sm:text-3xl">
                Comuni serviti in provincia di {dati.citta} ({dati.sigla})
              </h2>
              <ul className="mt-8 flex flex-wrap gap-2">
                {dati.comuni.map((c) => (
                  <li
                    key={c}
                    className="rounded-xs border border-border bg-background px-3 py-1.5 text-sm text-muted-foreground"
                  >
                    {c}
                  </li>
                ))}
              </ul>
              <p className="mt-6 max-w-2xl text-sm text-muted-foreground">
                Non trovi il tuo comune? Lavoriamo in tutta la provincia di {dati.citta} e nel resto
                della Lombardia: chiamaci e verifichiamo subito la disponibilità.
              </p>
            </div>
          </section>
        )}


        <section className="mx-auto max-w-6xl px-5 py-20">
          <p className="eyebrow">Domande frequenti a {dati.citta}</p>
          <h2 className="mt-3 max-w-2xl text-3xl font-semibold sm:text-4xl">
            Quello che ci chiedono più spesso.
          </h2>
          <div className="mt-10 grid gap-6 md:grid-cols-2">
            {dati.faq.map((f) => (
              <article key={f.domanda} className="surface-warm rounded-sm border border-border p-7">
                <h3 className="text-lg font-semibold">{f.domanda}</h3>
                <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{f.risposta}</p>
              </article>
            ))}
          </div>
        </section>


        <section className="mx-auto max-w-6xl px-5 py-20">
          <p className="eyebrow">Prima e dopo</p>
          <h2 className="mt-3 max-w-2xl text-3xl font-semibold sm:text-4xl">
            Lavori come quelli che facciamo a {dati.citta}.
          </h2>
          <div className="mt-12 grid gap-8 sm:grid-cols-2 lg:grid-cols-3">
            {lavori.map((l) => (
              <BeforeAfter key={l.after} {...l} />
            ))}
          </div>
        </section>

        <section id="contatti" className="mx-auto max-w-6xl scroll-mt-20 px-5 py-20">
          <div className="grid gap-12 md:grid-cols-2">
            <div>
              <p className="eyebrow">Preventivo</p>
              <h2 className="mt-3 text-3xl font-semibold sm:text-4xl">
                Richiedi un preventivo a {dati.citta}.
              </h2>
              <p className="mt-4 text-muted-foreground">
                Bastano un paio di foto e i metri quadri indicativi per una prima stima.
                Sopralluogo e preventivo sono gratuiti e senza impegno.
              </p>
              <div className="mt-8 space-y-3">
                <a href={`tel:${TEL_ANTONIO}`} className="surface-warm flex items-center justify-between rounded-sm border border-border px-5 py-4 transition-colors hover:border-primary">
                  <span className="font-medium">Antonio</span>
                  <span className="text-primary">+39 339 751 7813</span>
                </a>
                <a href={`tel:${TEL_LUCA}`} className="surface-warm flex items-center justify-between rounded-sm border border-border px-5 py-4 transition-colors hover:border-primary">
                  <span className="font-medium">Luca</span>
                  <span className="text-primary">+39 351 720 4660</span>
                </a>
                <a href={`mailto:${EMAIL}`} className="surface-warm flex items-center justify-between rounded-sm border border-border px-5 py-4 transition-colors hover:border-primary">
                  <span className="font-medium">Email</span>
                  <span className="text-primary">{EMAIL}</span>
                </a>
              </div>
              <div className="mt-6 flex flex-wrap items-center gap-4">
                <a
                  href={WHATSAPP_URL}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex rounded-xs bg-primary px-6 py-3 text-sm font-semibold text-primary-foreground transition-colors hover:bg-sienna"
                >
                  Scrivi ad Antonio su WhatsApp
                </a>
                <SocialLinks />
              </div>
              <p className="mt-6 text-xs text-muted-foreground">Pagina dedicata a {dati.citta} ({dati.sigla}) · {url}</p>
            </div>
            <PreventivoForm />
          </div>
        </section>
      </main>

      <SiteFooter />
    </div>
  );
}
